import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileText, Image as ImageIcon, Trash2, Send, Eye, CalendarDays, CheckCircle2, BarChart3, Mail, ArrowLeft } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { API, isMgmt } from '../lib/ops';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const wd = {
  list: () => axios.get(`${API}/weekly-dashboard`).then(r => r.data),
  get: (id) => axios.get(`${API}/weekly-dashboard/${id}`).then(r => r.data),
  del: (id) => axios.delete(`${API}/weekly-dashboard/${id}`).then(r => r.data),
  publish: (id) => axios.post(`${API}/weekly-dashboard/${id}/publish`).then(r => r.data),
};
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BARS = ['#F26522', '#3E2418', '#16a34a', '#2563eb'];
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

function StatusBadge({ status }) {
  const cls = status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`} data-testid="wd-status">{status === 'published' ? 'Published' : 'Draft'}</span>;
}

function UploadCard({ onDone }) {
  const [title, setTitle] = useState('');
  const [week, setWeek] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [autoSend, setAutoSend] = useState(true);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !file) { toast.error('Add a title and choose a file'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('week_ending', week);
    fd.append('notes', notes.trim());
    fd.append('auto_send', autoSend);
    try {
      await axios.post(`${API}/weekly-dashboard/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Dashboard uploaded');
      setTitle(''); setNotes(''); setFile(null);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E5E1DA] rounded-2xl p-5" data-testid="wd-upload-card">
      <div className="flex items-center gap-2 mb-3">
        <Upload className="w-4 h-4 text-[#F26522]" />
        <h2 className="text-base font-bold text-[#3E2418]">Upload this week's company dashboard</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-slate-500">Title
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Company Weekly Dashboard — Week 24" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="wd-title" />
        </label>
        <label className="text-xs text-slate-500">Week ending
          <input type="date" value={week} onChange={e => setWeek(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="wd-week" />
        </label>
      </div>
      <label className="text-xs text-slate-500 block mt-3">Notes (shown in the email & page)
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Highlights or a message for the team…" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="wd-notes" />
      </label>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-3">
        <label className="flex-1 text-xs text-slate-500">File (PDF, image, CSV or Excel)
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.csv,.xls,.xlsx" onChange={e => setFile(e.target.files?.[0] || null)} className="mt-1 w-full text-sm" data-testid="wd-file" />
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 font-medium">
          <input type="checkbox" checked={autoSend} onChange={e => setAutoSend(e.target.checked)} data-testid="wd-autosend" />
          Auto-send to company this Tuesday
        </label>
      </div>
      <Button onClick={submit} disabled={busy} className="mt-4 rounded-full bg-[#F26522] hover:bg-[#d9551a]" data-testid="wd-submit">
        {busy ? 'Uploading…' : 'Save dashboard'}
      </Button>
    </div>
  );
}

function StoreKpiCard() {
  const [sending, setSending] = useState(false);
  const previewUrl = `${BACKEND}/api/weekly-store-kpi/preview`;
  const send = async () => {
    if (!window.confirm('Send each store its weekly KPI email now?')) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/weekly-store-kpi/send`);
      toast.success(`Monday emails: ${r.data.sent} store(s) sent${r.data.skipped?.length ? `, ${r.data.skipped.length} skipped (no data)` : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };
  return (
    <div className="bg-white border border-[#E5E1DA] rounded-2xl p-5" data-testid="wd-storekpi-card">
      <div className="flex items-center gap-2 mb-1.5">
        <Mail className="w-4 h-4 text-[#F26522]" />
        <h2 className="text-base font-bold text-[#3E2418]">Monday store KPI emails</h2>
      </div>
      <p className="text-xs text-slate-500 mb-3">Every store gets its own weekly summary (net sales, labour %, food %, day parts vs last year) pulled automatically from Clearview — sent Mondays at 8am.</p>
      <div className="flex items-center gap-2">
        <a href={previewUrl} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full" data-testid="wd-storekpi-preview"><Eye className="w-4 h-4 mr-1" />Preview</Button></a>
        <Button onClick={send} disabled={sending} size="sm" className="rounded-full bg-[#3E2418] hover:bg-[#2a1810]" data-testid="wd-storekpi-send"><Send className="w-4 h-4 mr-1" />{sending ? 'Sending…' : 'Send now'}</Button>
      </div>
    </div>
  );
}

export default function WeeklyDashboard() {
  const { user } = useAuth();
  const mgmt = isMgmt(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    wd.list().then(r => setItems(r.dashboards || [])).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async (id) => {
    if (!window.confirm('Publish and email this dashboard to the whole company now?')) return;
    try {
      const r = await wd.publish(id);
      toast.success(`Published to ${r.recipients} recipient(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this dashboard?')) return;
    try { await wd.del(id); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="weekly-dashboard-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold">Company · Weekly</p>
        <h1 className="text-2xl sm:text-3xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Weekly Dashboard</h1>
        <p className="text-xs text-slate-500 mt-1">Company-wide dashboard published every Tuesday · store-specific KPIs emailed every Monday.</p>
      </div>

      {mgmt && (
        <div className="grid lg:grid-cols-2 gap-4">
          <UploadCard onDone={load} />
          <StoreKpiCard />
        </div>
      )}

      <div>
        <h2 className="text-sm font-bold text-[#3E2418] mb-2 flex items-center gap-2"><CalendarDays className="w-4 h-4" />Published & draft dashboards</h2>
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : items.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5E1DA] rounded-2xl p-8 text-center text-sm text-slate-400" data-testid="wd-empty">No dashboards yet.{mgmt ? ' Upload one above to get started.' : ''}</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="wd-list">
            {items.map(d => (
              <div key={d.id} className="bg-white border border-[#E5E1DA] rounded-2xl p-4 flex flex-col" data-testid={`wd-item-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {d.kind === 'data' ? <BarChart3 className="w-4 h-4 text-[#F26522] shrink-0" /> : d.kind === 'pdf' ? <FileText className="w-4 h-4 text-[#F26522] shrink-0" /> : <ImageIcon className="w-4 h-4 text-[#F26522] shrink-0" />}
                    <p className="font-bold text-sm text-[#3E2418] truncate">{d.title}</p>
                  </div>
                  <StatusBadge status={d.status} />
                </div>
                <p className="text-xs text-slate-500 mt-1">Week ending {fmtDate(d.week_ending)}{d.sent_to?.length ? ` · emailed ${d.sent_to.length}` : ''}</p>
                {d.notes && <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">{d.notes}</p>}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  <Link to={`/weekly-dashboard/${d.id}`}><Button variant="outline" size="sm" className="rounded-full" data-testid={`wd-view-${d.id}`}><Eye className="w-4 h-4 mr-1" />View</Button></Link>
                  {mgmt && (
                    <>
                      <Button onClick={() => publish(d.id)} size="sm" className="rounded-full bg-[#F26522] hover:bg-[#d9551a]" data-testid={`wd-publish-${d.id}`}><Send className="w-4 h-4 mr-1" />{d.status === 'published' ? 'Re-send' : 'Publish'}</Button>
                      <Button onClick={() => remove(d.id)} variant="ghost" size="sm" className="rounded-full text-rose-600 ml-auto" data-testid={`wd-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function WeeklyDashboardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    wd.get(id).then(setD).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="max-w-3xl p-8 text-center text-slate-400" data-testid="wd-detail-404">Dashboard not found or not published yet. <button onClick={() => navigate('/weekly-dashboard')} className="text-[#F26522] underline">Back</button></div>;
  if (!d) return <div className="max-w-3xl p-8 text-sm text-slate-400">Loading…</div>;

  const fileUrl = `${BACKEND}${d.file_url}`;
  const chart = d.parsed?.chart;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="weekly-dashboard-detail">
      <Link to="/weekly-dashboard" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#F26522]"><ArrowLeft className="w-4 h-4" />Back to dashboards</Link>
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold">Company Weekly Dashboard</p>
        <h1 className="text-2xl sm:text-3xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{d.title}</h1>
        <p className="text-xs text-slate-500 mt-1">Week ending {fmtDate(d.week_ending)} · uploaded by {d.created_by_name || 'Admin'}</p>
      </div>
      {d.notes && <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-sm text-slate-700" data-testid="wd-detail-notes">{d.notes}</div>}

      {chart && chart.series?.length > 0 && (
        <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4" data-testid="wd-detail-chart">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Data visualisation</p>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chart.labels.map((l, i) => ({ label: l, ...Object.fromEntries(chart.series.map(s => [s.name, s.data[i]])) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              {chart.series.map((s, i) => <Bar key={s.name} dataKey={s.name} fill={BARS[i % BARS.length]} radius={[4, 4, 0, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {d.parsed?.rows?.length > 0 && (
        <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4 overflow-x-auto" data-testid="wd-detail-table">
          <table className="w-full text-sm border-collapse">
            <thead><tr>{d.parsed.headers.map((h, i) => <th key={i} className="text-left px-3 py-2 bg-[#3E2418] text-white text-xs font-semibold first:rounded-l-md last:rounded-r-md">{h}</th>)}</tr></thead>
            <tbody>
              {d.parsed.rows.map((r, ri) => (
                <tr key={ri} className={ri % 2 ? 'bg-[#FAF8F5]' : ''}>{r.map((c, ci) => <td key={ci} className="px-3 py-1.5 border-b border-slate-100 text-slate-700">{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4" data-testid="wd-detail-file">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Uploaded file</p>
        {d.kind === 'pdf' ? (
          <iframe title="dashboard-pdf" src={fileUrl} className="w-full h-[600px] rounded-lg border border-slate-200" />
        ) : d.kind === 'image' ? (
          <img src={fileUrl} alt={d.title} className="w-full rounded-lg border border-slate-200" />
        ) : (
          <a href={fileUrl} className="inline-flex items-center gap-1 text-sm text-[#F26522] font-semibold" data-testid="wd-download-file"><FileText className="w-4 h-4" />Download {d.filename}</a>
        )}
      </div>
    </div>
  );
}
